import React, { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssistant,
  getAllAssistants,
  editAssistant,
} from "../../features/assistant/assistantSlice";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import AssistantDetailsForm from "./AssistantDetailsForm";
import { getPlatformsByAccount } from "../../features/platforms/platformsSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { Icons } from "../../constants/icon-library";
import { toast } from "react-toastify";
import Tool from "../Tools/Tools";

const Settings = lazy(() => import("../Setting/Settings"));
const Widgets = lazy(() => import("./AssistantWidget"));
const Configuration = lazy(() => import("../Configuration/Configuration"));

const AssistantDetail = () => {
  const { uuid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const TAB_STORAGE_KEY = `assistant_active_tab_${uuid}`;

  const { user } = useSelector((state) => state.user);
  const [userAssistant, setUserAssistant] = useState({});
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem(TAB_STORAGE_KEY) || "assistant";
  });

  const { platforms = [] } = useSelector((state) => state.platforms || {});

  const baseRoute = `/my-assistants/${uuid}`;
  const isNestedRoute = location.pathname !== baseRoute;

  useEffect(() => {
    if (user?.org) {
      dispatch(getAllAssistants(user.org));
      dispatch(getPlatformsByAccount(user.org));
    }
  }, [user, dispatch]);

  const tabs = [
    { key: "assistant", label: "Assistant", icon: "version" },
    { key: "configuration", label: "Configuration", icon: "system-activity" },
    { key: "tools", label: "Tools", icon: "tool" },
    // { key: "settings", label: "Settings", icon: "people" },
    // { key: "extensions", label: "Extension", icon: "extension" },
  ];

  useEffect(() => {
    const savedTab = sessionStorage.getItem(TAB_STORAGE_KEY);
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, [uuid, TAB_STORAGE_KEY]);

  useEffect(() => {
    if (activeTab) {
      sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
    }
  }, [activeTab, TAB_STORAGE_KEY]);

  useEffect(() => {
    if (!uuid) return;

    dispatch(getAssistant(uuid)).then((res) => {
      const assistant = res.payload;
      if (assistant) {
        setUserAssistant(assistant);
      }
    });
  }, [uuid, dispatch]);

  const handleVisibilityToggle = async () => {
    const newValue = !userAssistant.isPublic;
    try {
      await dispatch(
        editAssistant({
          uuid,
          data: { isPublic: newValue },
        }),
      ).unwrap();

      setUserAssistant((prev) => ({
        ...prev,
        isPublic: newValue,
      }));

      toast.success(`Assistant set to ${newValue ? "Public" : "Private"}`);
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(baseRoute, { replace: true });
  };

  const handleAssistantUpdated = () => {
    setUserAssistant((prev) => ({
      ...prev,
    }));
  };

  return (
    <div className="content px-2">
      {uuid && (
        <div className="pb-5">
          <div className="row g-4">
            <div className="ps-0 m-0">
              <div className="d-flex flex-column tab-container sticky-tabs">
                <div className="d-flex assistant-header justify-content-between align-items-center ms-3 mt-3 pb-2">
                  <h2>{userAssistant.name}</h2>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch mb-0 me-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={!!userAssistant.isPublic}
                        onChange={handleVisibilityToggle}
                        style={{ transform: "scale(1.4)" }}
                      />
                      <label className="form-check-label fw-bold me-2">
                        {userAssistant.isPublic ? "Public" : "Private"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="scrollable-tabs">
                  <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                    {tabs.map((tab) => (
                      <li className="nav-item" key={tab.key}>
                        <button
                          type="button"
                          role="tab"
                          className={`nav-link ${
                            activeTab === tab.key ? "active" : ""
                          }`}
                          onClick={() => handleTabChange(tab.key)}
                        >
                          <i
                            className={`wb ${Icons(
                              tab.icon,
                            )} fs-8 me-2 mt-3 align-middle`}
                          ></i>
                          <span className="tab-label">{tab.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {isNestedRoute ? (
              <Outlet
                context={{ onAssistantUpdated: handleAssistantUpdated }}
              />
            ) : (
              <>
                {activeTab === "assistant" && (
                  <AssistantDetailsForm
                    assistantId={uuid}
                    user={user}
                    platforms={platforms}
                    onAssistantUpdated={() => {
                      setUserAssistant((prev) => ({
                        ...prev,
                      }));
                    }}
                  />
                )}

                <Suspense fallback={<LoadingComponent loading={true} />}>
                  {activeTab === "settings" && (
                    <Settings
                      assistantId={uuid}
                      assistant={userAssistant}
                      activeTab={activeTab}
                    />
                  )}
                  {activeTab === "extensions" && (
                    <Widgets
                      widgetName={userAssistant.platform?.type}
                      assistantId={uuid}
                      agentType={userAssistant.agentType}
                    />
                  )}
                  {activeTab === "configuration" && (
                    <Configuration configType="assistant" configId={uuid} />
                  )}
                  {activeTab === "tools" && (
                    <Tool
                      assistantId={uuid}
                      onAssistantUpdated={() => {
                        setUserAssistant((prev) => ({
                          ...prev,
                        }));
                      }}
                    />
                  )}
                </Suspense>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantDetail;
